import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Camera, FileText, Plus, X, RotateCcw, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';

interface InvoicePhotoCaptureProps {
  photos: string[];
  onPhotosChange: (photos: string[]) => void;
}

export const InvoicePhotoCapture = ({ photos, onPhotosChange }: InvoicePhotoCaptureProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const triggerCamera = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    
    try {
      // Upload to invoice-documents bucket
      const fileName = `invoice-${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('invoice-documents')
        .upload(fileName, file);

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('invoice-documents')
        .getPublicUrl(fileName);

      const newPhotos = [...photos, urlData.publicUrl];
      onPhotosChange(newPhotos);
      
      toast({
        title: "Invoice Page Captured",
        description: `Page ${newPhotos.length} added successfully`,
      });
    } catch (error: any) {
      console.error('Photo upload failed:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload photo",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(newPhotos);
    setSelectedPhotoIndex(null);
    
    toast({
      title: "Photo Removed",
      description: "Invoice page has been removed",
    });
  };

  const retakePhoto = (index: number) => {
    setSelectedPhotoIndex(index);
    triggerCamera();
  };

  const replacePhoto = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = event.target.files?.[0];
    if (!file || selectedPhotoIndex === null) return;

    setIsUploading(true);
    
    try {
      // Upload replacement photo
      const fileName = `invoice-${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('invoice-documents')
        .upload(fileName, file);

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('invoice-documents')
        .getPublicUrl(fileName);

      const newPhotos = [...photos];
      newPhotos[index] = urlData.publicUrl;
      onPhotosChange(newPhotos);
      setSelectedPhotoIndex(null);
      
      toast({
        title: "Photo Replaced",
        description: `Page ${index + 1} has been updated`,
      });
    } catch (error: any) {
      console.error('Photo replacement failed:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to replace photo",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <FileText className="w-12 h-12 mx-auto text-primary" />
        <h2 className="text-2xl font-semibold">Invoice Photos</h2>
        <p className="text-muted-foreground">
          Capture all pages of the invoice document
        </p>
        {photos.length > 0 && (
          <Badge variant="secondary" className="text-sm">
            {photos.length} page{photos.length !== 1 ? 's' : ''} captured
          </Badge>
        )}
      </div>

      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-medium">Invoice Pages</h3>
          <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-3'}`}>
            {photos.map((photo, index) => (
              <Card key={index} className="relative overflow-hidden">
                <CardContent className="p-0">
                  <div className="relative">
                    <img
                      src={photo}
                      alt={`Invoice page ${index + 1}`}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute top-2 left-2">
                      <Badge variant="secondary" className="text-xs">
                        Page {index + 1}
                      </Badge>
                    </div>
                    <div className="absolute top-2 right-2 flex gap-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => retakePhoto(index)}
                        className="h-8 w-8 p-0"
                        disabled={isUploading}
                      >
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => removePhoto(index)}
                        className="h-8 w-8 p-0"
                        disabled={isUploading}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="text-sm text-muted-foreground">
                      Page {index + 1} of {photos.length}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Camera Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={selectedPhotoIndex !== null ? 
          (e) => replacePhoto(e, selectedPhotoIndex) : 
          handlePhotoCapture
        }
        className="hidden"
        disabled={isUploading}
      />

      {/* Action Buttons */}
      <div className="space-y-3">
        {photos.length === 0 ? (
          <Button
            onClick={triggerCamera}
            disabled={isUploading}
            className="w-full h-14 text-lg"
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3" />
                Uploading...
              </>
            ) : (
              <>
                <Camera className="w-5 h-5 mr-3" />
                Capture First Page
              </>
            )}
          </Button>
        ) : (
          <Button
            onClick={triggerCamera}
            variant="outline"
            disabled={isUploading}
            className="w-full h-12"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Another Page
          </Button>
        )}

        {/* Tips */}
        <Card className="bg-muted/50">
          <CardContent className="p-4">
            <h4 className="font-medium mb-2">📷 Photo Tips</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Hold device steady and ensure good lighting</li>
              <li>• Capture the entire document in frame</li>
              <li>• Multiple pages? Capture each page separately</li>
              <li>• Tap to retake any unclear photos</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};